import React, { useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { navBarStateSelector as stateSelector } from 'reduxModules/navBar/selectors'
import dispatchers from 'reduxModules/navBar/dispatchers'
import PropTypes from 'prop-types'
import _ from 'lodash'

import { Link, useNavigate } from 'react-router-dom'
import CookieNotice from '../../generic/CookieNotice'
import SubscribeDialogCTA from '../SubscribeDialogCTA'
import keyUpHandler from '../../utils/keyUpHandler'
import MenuItem from './MenuItem/index.jsx'
import SearchBox from './SearchBox'
import getOptions from './options'
import logoMain from '../../../global/assets/images/logo/sw-logo.svg'
import CartModal from '../../Cart/CartModal'
import { getActiveSection } from './utils'
import AccountMenu from './AccountMenu'

import './styles.css'

export function NavBar(props) {
  const { pathName, openedSearchBox = false, hideInnerNav } = props
  const navigate = useNavigate()

  const isContest = useCallback(
    () => pathName && pathName.startsWith('/contest'),
    [pathName]
  )

  const showNav = useCallback(() => {
    if (isContest()) return false
    return !openedSearchBox && !hideInnerNav
  }, [isContest, openedSearchBox, hideInnerNav])

  const flowContest = useCallback(() => {
    if (isContest()) return pathName && pathName.endsWith('/thank-you')
    return true
  }, [isContest, pathName])

  const inputKeyUp = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        const value = event.currentTarget.value
        navigate(`/search?q=${encodeURIComponent(value)}`)
        props.toggleSearchBox()
        props.clearSuggestions()
      }
      if (event.key === 'Escape') {
        props.toggleSearchBox()
        props.clearSuggestions()
      }
    },
    [navigate, props]
  )

  useEffect(() => {
    if (props.getInitialShowCookieNoticeState)
      props.getInitialShowCookieNoticeState()
  }, [props.getInitialShowCookieNoticeState])
  const hasMobileSubsCTA =
    !props.isSubscriber &&
    props.dimensions &&
    props.dimensions.width < 870 &&
    props.pathName !== '/checkout' &&
    // SPEC-052: redesigned lead-magnet pages carry their own trial CTAs
    !(props.pathName?.startsWith('/lm/') && props.isRedesignedLeadMagnet)
  const headerClasses = [
    props.isLoggedIn &&
    props.userSelector &&
    !props.userSelector.isVerified &&
    props.pathName !== '/checkout'
      ? 'not-verified'
      : '',
    props.isFullscreen ? 'is-fullscreen' : '',
    hasMobileSubsCTA ? 'with-mobile-subscribe-cta' : '',
    props.showCookieNotice && props.dimensions && props.dimensions.width < 870
      ? 'with-cookie-notice'
      : '',
    props.isLoggedIn &&
    props.userSelector &&
    !props.userSelector.watchedWalkthroughVideo &&
    props.pathName !== '/checkout'
      ? 'have-not-watched-walkthrough'
      : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <header id="NavBar" className={headerClasses}>
      {props.showCookieNotice && (
        <CookieNotice hideCookieNotice={props.hideCookieNotice} />
      )}
      {hasMobileSubsCTA && (
        <SubscribeDialogCTA>
          <div className="mobile-subscribe-cta">
            <span>
              {props.usedFreeTrial ? 'Subscribe now' : 'Start Free 7-Day Trial'}
            </span>
          </div>
        </SubscribeDialogCTA>
      )}
      <div
        className={`wrapper${
          props.hideInnerNav && props.isMobile ? ' center-logo' : ''
        }`}
      >
        {!props.hideInnerNav && !props.openedSearchBox && (
          <div
            className={`burger-menu${
              props.openedSearchBox ? ' shrink-burger' : ''
            }`}
          >
            <button
              type="button"
              className="burger-icon"
              onClick={
                !props.showMobileMenu && props.isMobile
                  ? () => props.toggleMobileMenu()
                  : null
              }
            >
              &#9776;
            </button>
          </div>
        )}

        {!(props.openedSearchBox && props.isMobile) && (
          <div className="main-menu">
            <Link
              className={`logo${props.isMobile ? ' -mobile' : ''}${
                hasMobileSubsCTA ? ' with-mobile-subscribe-cta' : ''
              }`}
              to="/"
              tabIndex={0}
            >
              <img src={logoMain} alt="Stan Winston School" />
            </Link>

            {showNav() && (
              <nav id="internalNav">
                <ul className="nav">
                  {getOptions(false).map((option) => (
                    <MenuItem
                      key={option.title}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...option}
                      activeSection={getActiveSection(props.pathName)}
                    />
                  ))}
                </ul>
              </nav>
            )}
          </div>
        )}

        {!props.hideInnerNav && (
          <div
            className={`account-menu${
              props.openedSearchBox ? ' account-menu--search' : ''
            }`}
          >
            {!_.isEmpty(props.itemAdded) && (
              <CartModal
                itemAdded={props.itemAdded}
                closeItemModal={props.closeItemModal}
              />
            )}

            {props.openedSearchBox && flowContest() && (
              <SearchBox
                inputKeyUp={inputKeyUp}
                toggleSearchBox={props.toggleSearchBox}
                getSuggestions={props.getSearchSuggestions}
                autocompleteResults={props.autocompleteResults}
                clearSuggestions={props.clearSuggestions}
              />
            )}

            {!props.isSubscriber &&
              props.dimensions &&
              props.dimensions.width >= 870 &&
              flowContest() &&
              !props.openedSearchBox && (
                <SubscribeDialogCTA>
                  <div className="subscribe-cta">
                    <span>Subscribe</span>
                  </div>
                </SubscribeDialogCTA>
              )}

            {!props.openedSearchBox && (
              <div className="account-actions">
                {!props.openedSearchBox && !isContest() && (
                  <button
                    type="button"
                    aria-label="Toggle search box"
                    onClick={() => props.toggleSearchBox()}
                    onKeyUp={(event) =>
                      keyUpHandler(event, props.toggleSearchBox)
                    }
                    tabIndex={0}
                  >
                    <i
                      aria-hidden="true"
                      className="account-actions__action icon-search in-navbar"
                    />
                  </button>
                )}

                {!props.openedSearchBox &&
                  !props.isLibrarySubscriber &&
                  !isContest() && (
                    <Link
                      aria-label={`Go to cart, ${
                        props.cartCount ? props.cartCount : '0'
                      } items in cart`}
                      className="account-actions__action cart"
                      to="/cart"
                    >
                      <i className="icon-cart" aria-hidden="true" />
                      {props.cartCount ? (
                        <span className="cart-count">
                          {props.cartCount > 99 ? '+99' : props.cartCount}
                        </span>
                      ) : null}
                    </Link>
                  )}

                {!props.openedSearchBox &&
                  props.isLoggedIn &&
                  !props.isMobile &&
                  !isContest() && (
                    <AccountMenu
                      logout={props.logout}
                      createdByProxy={props.createdByProxy}
                    />
                  )}

                {!props.openedSearchBox &&
                  !props.isLoggedIn &&
                  !isContest() && (
                    <Link
                      className="account-actions__action login-button"
                      to={{
                        pathname: '/login',
                        state: { next: props.pathName }
                      }}
                    >
                      login
                    </Link>
                  )}
              </div>
            )}

            {props.isLoggedIn && !props.openedSearchBox && props.hijacking && (
              <button
                type="button"
                onClick={() => props.releaseHijacked()}
                className="btn -mini -orange release-button"
                onKeyUp={(event) => keyUpHandler(event, props.releaseHijacked)}
                tabIndex={0}
              >
                Release
              </button>
            )}
          </div>
        )}
      </div>
    </header>
  )
}

NavBar.propTypes = {
  openedSearchBox: PropTypes.bool,
  pathName: PropTypes.string.isRequired,
  toggleSearchBox: PropTypes.func.isRequired,
  releaseHijacked: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func,
  getInitialShowCookieNoticeState: PropTypes.func
}

export default connect(stateSelector, dispatchers)(NavBar)
